// Import design tokens
import '@ds/brand-tokens'
// Import typography
import '@ds/typography'
// Import Tailwind theme styles
import '@ds/tailwind-theme/styles'

import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { createStore } from 'vuex'
import { createHead } from '@unhead/vue/client'
import VueSafeHtml from '@/base/plugins/safe-html'

import storeModules from '@/base/store/modules'
import { setStore } from '@/base/store/storeRegistry'
import { routes } from '@/base/const/routing.const'
import '@/base/polyfills/paymentrequest.js'
import { YOURCX_CONFIG } from '@/base/const/yourcx.const'
import { IS_MAINTENANCE_GLOBAL } from '@/base/const/maintenance.const'
import i18n, { loadCurrentLanguage } from '@/base/i18n/index'
import { setupVeeValidate } from '@/base/validation/setup'
import { COMMERCIAL_BREAK_REDIRECT_TIME_LONG, CONFIRMATION_REDIRECT_TIME_LONG } from '@/base/const/confirmation.const'
import MaskDirective from '@/base/directives/mask.directive'
import { setAppConfig } from '@/base/config/app-config'
import { mergeContextConfig } from '@/base/utils/contextConfig'
import { initSentry } from '@/base/utils/sentryInit'

export const initContext = async (contextName, contextApp, customConfig = {}) => {
  const APP = contextApp
  const CONTEXT = contextName || 'payment'

  const app = createApp(APP)

  app.config.globalProperties.HIDE_TRANSACTION_INFO = false
  app.config.globalProperties.HIDE_MARKETING = false
  app.config.globalProperties.USE_APP_BLUEMEDIA = false
  app.config.globalProperties.CARD_IFRAME_URL = null

  if (customConfig && typeof customConfig === 'object') {
    Object.entries(customConfig).forEach(([key, value]) => {
      app.config.globalProperties[key] = value
      setAppConfig(key, value)
    })
  }

  const ROUTER_OWNED_COMPONENTS = new Set(['RouterView', 'RouterLink', 'router-view', 'router-link'])

  if (APP?.components) {
    Object.keys(APP.components).forEach((name) => {
      if (ROUTER_OWNED_COMPONENTS.has(name)) return
      app.component(name, APP.components[name])
    })
  }

  app.directive('mask', MaskDirective)
  setupVeeValidate(app)

  const baseUrl = (import.meta.env.BASE_URL || '/web/') + CONTEXT

  const router = createRouter({
    history: createWebHistory(baseUrl),
    routes,
  })

  router.beforeEach(async (to, from, next) => {
    try {
      // Aktualnie zażądany język, NIE `currentLang` - ten jest zamrożony na języku przeglądarki,
      // więc każda nawigacja SPA kasowała język ustawiony z transakcji (patrz i18n/index.js).
      await loadCurrentLanguage()
      if (IS_MAINTENANCE_GLOBAL && to.name !== 'Maintenance') {
        next({ name: 'Maintenance' })
      } else {
        next()
      }
    } catch (error) {
      console.error('failed to load language context-init router beforeEach:', error)
      next()
    }
  })

  const store = createStore({
    modules: storeModules,
    strict: false,
  })
  setStore(store)

  const head = createHead()

  // Konfiguracja Sentry (przypięta wersja SDK, wyciszenia, filtry `beforeSend`) siedzi
  // w `utils/sentryInit.js`. Poza produkcją to wywołanie jest no-opem.
  initSentry(app, CONTEXT)

  app.config.globalProperties.$VUE_CONTEXT_NAME = CONTEXT

  // Konteksty przekazują tę konfigurację w `customConfig` pod kluczem bez prefiksu, a komponenty
  // czytają ją pod `$VUE_*` — bez tego scalenia wygrywała konfiguracja bazowa i ustawienia kontekstu
  // były ignorowane (np. wyłączona ankieta YourCX i tak się uruchamiała). Szczegóły i powód scalania
  // zamiast podmiany: `utils/contextConfig.js`.
  app.config.globalProperties.$VUE_YOURCX_CONFIG = mergeContextConfig(YOURCX_CONFIG, customConfig?.YOURCX_CONFIG)

  const isMockEnabled = () => import.meta.env.VITE_APP_USE_API_MOCK === 'true'
  const isDevelopmentMode = () => import.meta.env.DEV

  if (isMockEnabled() && isDevelopmentMode()) {
    app.config.globalProperties.$VUE_CONFIRMATION_TIMER = CONFIRMATION_REDIRECT_TIME_LONG
    app.config.globalProperties.$VUE_COMMERCIAL_BREAK_REDIRECT_TIME = COMMERCIAL_BREAK_REDIRECT_TIME_LONG

    try {
      const mswModule = await import('../mock/initMsw')
      await mswModule.startWorker()
    } catch (error) {
      console.error('MSW mock loading failed:', error)
    }
  }

  app.use(router)
  app.use(store)
  app.use(i18n)
  app.use(head)
  app.use(VueSafeHtml)

  app.config.globalProperties.$t = i18n.global.t
  app.config.globalProperties.$tc = i18n.global.tc
  app.config.globalProperties.$te = i18n.global.te
  app.config.globalProperties.$d = i18n.global.d
  app.config.globalProperties.$n = i18n.global.n
  app.config.globalProperties.$i18n = i18n.global

  if (import.meta.env.DEV) {
    app.config.performance = true
  }

  // Brak własnego app.config.errorHandler jest tu celowy: Sentry.init({ app }) podpina swój handler,
  // który łapie błędy komponentów automatycznie (razem z nazwą komponentu, hookiem i propsami).
  // Przypisanie własnego PO Sentry.init nadpisałoby tamten i wyłączyło zbieranie błędów Vue.
  // Szum __disposer odsiewa ignoreErrors w Sentry.init.

  if (import.meta.env.DEV) {
    app.config.warnHandler = (msg, instance, trace) => {
      console.warn('vue warning:', msg, trace)
    }
  }

  const mountApp = async () => {
    const appElement = document.querySelector('#app')
    if (!appElement) {
      console.error('element #app not found')
      return
    }

    try {
      await router.isReady()
      app.mount('#app')
    } catch (error) {
      console.error('app mount error:', error)
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountApp)
  } else {
    void mountApp()
  }
}

export default initContext
